import { usePermissions } from '../lib/permissions';
import { useWorkspace } from '../context/WorkspaceContext';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../context/ProfileContext';

export const useProjectPermissions = () => {
  const { selectedTeam, selectedProject } = useWorkspace();
  const { user } = useAuth();
  const { profileData } = useProfile();
  
  const { permissions, role } = usePermissions(selectedTeam, user?.id, profileData?.id);

  return {
    // Project management permissions
    canCreateProject: permissions.canManageProjects,
    canEditProject: permissions.canManageProjects,
    canDeleteProject: permissions.canManageProjects,

    // Key management permissions
    canCreateKey: permissions.canManageKeys,
    canEditKey: permissions.canManageKeys,
    canDeleteKey: permissions.canManageKeys,

    // String management permissions
    canCreateString: permissions.canManageStrings,
    canEditString: permissions.canManageStrings,
    canDeleteString: permissions.canManageStrings,

    // Language permissions
    canViewAllStrings: permissions.canManageStrings,
    canEditLanguages: permissions.canEditLanguages,
    canManageLanguages: permissions.canManageLanguages,

    // Review permissions
    canViewReviews: permissions.canViewReviews,
    canEditReviews: permissions.canEditReviews,
    canManageReviews: permissions.canManageReviews,

    // Team management permissions
    canManageTeam: permissions.canManageTeam,
    canManageSettings: permissions.canManageSettings,
    canManageBilling: permissions.canManageBilling,
    canManageIntegrations: permissions.canManageIntegrations,
    canManageWebhooks: permissions.canManageWebhooks,
    canManageAPIKeys: permissions.canManageAPIKeys,
    canManageRoles: permissions.canManageRoles,

    // Role information
    role,

    // Helper functions
    canManageProject: (projectId: string) => {
      if (!selectedProject || selectedProject.id !== projectId) return false;
      return permissions.canManageProjects;
    },

    canManageKey: (keyId: string) => {
      return permissions.canManageKeys;
    },

    canManageString: (stringId: string) => {
      return permissions.canManageStrings;
    },

    canEditLanguage: (language: string) => {
      return permissions.canEditLanguages;
    },

    // Owner-specific helper functions
    canManageTeamSettings: () => {
      return permissions.canManageTeam;
    },

    canManageBillingSettings: () => {
      return permissions.canManageBilling;
    },

    canManageIntegrationSettings: () => {
      return permissions.canManageIntegrations;
    },

    canManageWebhookSettings: () => {
      return permissions.canManageWebhooks;
    },

    canManageAPIKeySettings: () => {
      return permissions.canManageAPIKeys;
    },

    // Role-specific helper functions
    isOwner: role === 'owner',
    isAdmin: role === 'admin',
    isReviewer: role === 'reviewer'
  };
}; 